import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiErrorMessage, coursesApi, type CourseLesson } from '../../api/client'
import { useAuthStore } from '../../store/authStore'
import { BookOpen, Play, Lock, CheckCircle, Clock, Users, Award } from 'lucide-react'
import toast from 'react-hot-toast'

export default function CourseDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: course, isLoading, isError, refetch } = useQuery({
    queryKey: ['course', slug, user?.id || 'anonymous'],
    queryFn: () => coursesApi.get(slug!).then(r => r.data),
    enabled: !!slug,
  })
  const { data: progress } = useQuery({
    queryKey: ['course-progress', slug, user?.id],
    queryFn: () => coursesApi.getCourseProgress(slug!).then(r => r.data),
    enabled: !!slug && !!user && !!course?.is_enrolled,
  })

  const enrollMut = useMutation({
    mutationFn: () => coursesApi.enroll(slug!),
    onSuccess: (res) => {
      toast.success(res.data.message)
      qc.invalidateQueries({ queryKey: ['course', slug] })
    },
    onError: (error: unknown) => toast.error(apiErrorMessage(error, 'Enrollment failed')),
  })

  if (isLoading) return <div className="max-w-4xl mx-auto px-4 py-20 text-brand-muted">Loading…</div>
  if (isError) return <div className="max-w-4xl mx-auto px-4 py-20" role="alert">Unable to load this course. <button onClick={() => refetch()} className="btn-outline">Retry</button></div>
  if (!course) return <div className="max-w-4xl mx-auto px-4 py-20 text-brand-muted">Course not found.</div>

  const totalLessons = course.lesson_count
  const canManage = user?.role === 'admin' || user?.id === course.instructor.id
  const lessons = course.modules.flatMap(module => module.lessons)
  const continueLesson = lessons.find(lesson => lesson.is_accessible && !lesson.is_completed)
    || [...lessons].reverse().find(lesson => lesson.is_accessible)

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="grid md:grid-cols-3 gap-8">
        {/* Main content */}
        <div className="md:col-span-2">
          <div className="flex gap-2 flex-wrap mb-4">
            <span className="badge badge-blue">{course.level}</span>
            {!course.is_enrollment_open ? <span className="badge badge-amber">Coming soon</span> : course.is_free && <span className="badge badge-green">Free</span>}
            {course.tags?.map((t: string) => <span key={t} className="badge badge-blue">{t}</span>)}
          </div>
          <h1 className="text-3xl font-extrabold mb-2">{course.title}</h1>
          {course.subtitle && <p className="text-lg text-brand-muted mb-4">{course.subtitle}</p>}
          <div className="flex gap-5 text-sm text-brand-muted mb-6">
            <span className="flex items-center gap-1.5"><BookOpen size={14}/>{totalLessons} lessons</span>
            <span className="flex items-center gap-1.5"><Users size={14}/>{course.enrolled_count} enrolled</span>
            <span className="flex items-center gap-1.5">Managed by {course.instructor.username === 'admin' ? 'PacketFive Academy' : course.instructor.full_name || course.instructor.username}</span>
          </div>

          {course.description && (
            <div className="mb-8">
              <h2 className="text-lg font-bold mb-3">About this course</h2>
              <p className="text-brand-muted leading-relaxed">{course.description}</p>
            </div>
          )}

          {(course.book_url || course.lab_url) && (
            <div className="card mb-8">
              <h2 className="text-lg font-bold mb-3">Book and companion labs</h2>
              <p className="text-sm text-brand-muted mb-3">The book is available on Amazon. Its manuscript is not included here; public companion labs retain their own repository licence.</p>
              <div className="flex gap-3 flex-wrap">
                {course.book_url && <a className="btn-outline" href={course.book_url} target="_blank" rel="noopener noreferrer">Book and Amazon editions</a>}
                {course.lab_url && <a className="btn-outline" href={course.lab_url} target="_blank" rel="noopener noreferrer">Labs on GitHub</a>}
              </div>
            </div>
          )}

          {/* Curriculum */}
          <div>
            <h2 className="text-lg font-bold mb-4">Curriculum</h2>
            {!!lessons.length && <p className="text-sm text-brand-muted mb-4">Lessons unlock in order. Complete each teaching lesson and lab, then pass its chapter knowledge check to continue.</p>}
            {progress && <p className="text-sm mb-4" role="status">{progress.completed_lessons} of {progress.total_lessons} lessons completed ({progress.progress_percent}%).</p>}
            <div className="space-y-3">
              {course.modules?.map(mod => (
                <div key={mod.id} className="border border-brand-border rounded-xl overflow-hidden">
                  <div className="bg-brand-surface2 px-4 py-3 font-semibold text-sm flex items-center gap-2">
                    {!!mod.lessons.length && mod.lessons.every(lesson => lesson.is_completed) && <CheckCircle size={16} className="text-brand-green" aria-label="Chapter completed" />}
                    {mod.title}
                  </div>
                  {mod.lab_url && <div className="px-4 py-3 text-sm"><a href={mod.lab_url} target="_blank" rel="noopener noreferrer" className="text-brand-blue">Open companion lab on GitHub</a></div>}
                  <div className="divide-y divide-brand-border">
                    {mod.lessons?.map(lesson => (
                      <LessonRow key={lesson.id} lesson={lesson} slug={slug!} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div>
          <div className="card sticky top-20">
            {course.thumbnail_url && (
              <img src={course.thumbnail_url} alt={course.title} className="w-full rounded-lg mb-4 aspect-video object-cover" />
            )}
            {course.is_enrolled || canManage ? (
              <div>
                <div className="flex items-center gap-2 text-brand-green text-sm font-semibold mb-3">
                  <CheckCircle size={16}/> {canManage ? 'Instructor review access' : "You're enrolled"}
                </div>
                {continueLesson && (
                  <button className="btn-primary w-full justify-center"
                    onClick={() => navigate(`/learn/${slug}/lessons/${continueLesson.id}`)}>
                    <Play size={16}/> {progress?.completed ? 'Review Course' : 'Continue Learning'}
                  </button>
                )}
              </div>
            ) : (
              <div>
                <div className="text-2xl font-extrabold mb-1">{!course.is_enrollment_open ? 'Coming soon' : course.is_free ? 'Free' : 'Premium'}</div>
                {!course.is_enrollment_open ? (
                  <p className="text-sm text-brand-muted mt-3" role="status">Enrolment is not open. Fees and assessment requirements will be announced before launch.</p>
                ) : course.is_enrollment_pending ? (
                  <p className="text-sm text-brand-muted mt-3" role="status">Your access request is recorded. Payment and administrator approval are still required.</p>
                ) : user ? (
                  <button className="btn-primary w-full justify-center" onClick={() => enrollMut.mutate()} disabled={enrollMut.isPending}>
                    {enrollMut.isPending ? 'Submitting…' : course.is_free ? 'Enroll Now - Free' : 'Request Paid Access'}
                  </button>
                ) : (
                  <Link to={`/register?course=${encodeURIComponent(slug!)}`} className="btn-primary w-full justify-center text-center block">
                    Sign up to Enroll
                  </Link>
                )}
                {progress?.certificate_available && <Link to="/certificates" className="btn-outline w-full justify-center mt-3"><Award size={16}/> Your completion certificate</Link>}
                {course.is_enrollment_open && !course.is_free && <p className="text-brand-muted text-xs mt-3">Paid content stays locked until payment is confirmed by an administrator. For payment arrangements, contact <a href="mailto:connect@packetfive.com" className="text-brand-blue">connect@packetfive.com</a>.</p>}
              </div>
            )}
            <div className="mt-4 space-y-2 text-sm text-brand-muted">
              <a className="text-brand-blue" href="https://academy.packetfive.com/certificates#course">Preview Academy course certificate</a>
              {course.is_enrollment_open && <div className="flex items-center gap-2"><Award size={14}/> Certificate on completion</div>}
              <div className="flex items-center gap-2"><Clock size={14}/> Self-paced</div>
              <div className="flex items-center gap-2"><BookOpen size={14}/>{totalLessons} lessons</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function LessonRow({ lesson, slug }: { lesson: CourseLesson; slug: string }) {
  const navigate = useNavigate()
  const accessible = lesson.is_accessible
  const icon = lesson.is_completed ? <CheckCircle size={14} className="text-brand-green" aria-label="Completed"/>
    : accessible ? <Play size={14} className="text-brand-blue"/> : <Lock size={14} className="text-brand-muted" aria-label="Locked"/>

  return (
    <button
      disabled={!accessible}
      title={lesson.lock_reason || undefined}
      className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors ${accessible ? 'hover:bg-brand-surface2 cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
      onClick={() => accessible && navigate(`/learn/${slug}/lessons/${lesson.id}`)}
    >
      {icon}
      <span className="flex-1">{lesson.title}</span>
      {lesson.is_preview && <span className="badge badge-green text-xs">Preview</span>}
      {lesson.has_quiz && <span className="badge badge-amber text-xs">Quiz</span>}
      {lesson.video_duration_seconds && (
        <span className="text-xs text-brand-muted">{Math.round(lesson.video_duration_seconds / 60)}m</span>
      )}
    </button>
  )
}
